import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Anchor, Box, Collapse, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { SurfaceCard } from '@/design/SurfaceCard';
import { formatPct } from './PolicyFormatting';
import { STATUS_VISUAL } from './PolicyStatusVisual';
import type { PolicyView } from './PolicyTypes';

const EDIT_ROUTE = '/classification/governance';
const detailRoute = (policyId: string): string => `/governance/policy/${policyId}`;
const DISTRIBUTION_DISPLAY = 6;

/** A compact verdict-volume sparkline (oldest → newest), in the policy's status hue. */
function Sparkline({ values, color }: { values: number[]; color: string }): ReactElement | null {
    if (values.length < 2) return null;
    const w = 96;
    const h = 28;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const step = w / (values.length - 1);
    const pts = values.map((v, i) => [i * step, h - ((v - min) / span) * h] as const);
    const line = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
    const area = `${line} L${w},${h} L0,${h} Z`;
    return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ flexShrink: 0 }} aria-hidden>
            <path d={area} fill={color} opacity={0.12} />
            <path d={line} fill="none" stroke={color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
        </svg>
    );
}

function StatusBadge({ view }: { view: PolicyView }): ReactElement {
    const visual = STATUS_VISUAL[view.status];
    const label = view.concernTurns > 0 ? `${view.concernTurns.toLocaleString()} flagged` : visual.label;
    return (
        <Box
            style={{
                padding: '1px 9px',
                borderRadius: 999,
                background: visual.soft,
                border: `1px solid ${visual.border}`,
                color: visual.text,
                fontSize: 11,
                fontWeight: 700,
                whiteSpace: 'nowrap',
            }}
        >
            {label}
        </Box>
    );
}

/**
 * One policy in the register: the live classification's name + statement, its REAL
 * classified activity (verdict volume, status from the verdict mix, confidence, trend),
 * and an expand-in-place verdict distribution with concerning values highlighted.
 */
export default function PolicyRegisterRow({ view }: { view: PolicyView }): ReactElement {
    const [open, setOpen] = useState(false);
    const visual = STATUS_VISUAL[view.status];
    const Icon = view.icon;
    const Chevron = open ? ChevronDown : ChevronRight;
    const hasData = view.turns > 0;
    const shown = view.distribution.slice(0, DISTRIBUTION_DISPLAY);
    const maxCount = view.distribution.reduce((m, d) => Math.max(m, d.count), 0);

    return (
        <SurfaceCard $padding="md" style={{ borderTop: `2px solid ${visual.solid}`, gap: 0 }}>
            <UnstyledButton onClick={() => setOpen((o) => !o)} aria-expanded={open} style={{ width: '100%' }}>
                <Group justify="space-between" wrap="nowrap" align="center" gap="md">
                    <Group wrap="nowrap" align="center" gap="md" style={{ flex: 1, minWidth: 0 }}>
                        <Box
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 36,
                                height: 36,
                                flexShrink: 0,
                                background: visual.soft,
                                border: `1px solid ${visual.border}`,
                                borderRadius: 'var(--bm-radius)',
                            }}
                        >
                            <Icon size={18} color={visual.solid} />
                        </Box>
                        <Box style={{ minWidth: 0 }}>
                            <Text fw={700} size="sm" truncate>
                                {view.policy.name}
                            </Text>
                            {view.policy.description && (
                                <Text c="dimmed" size="xs" truncate>
                                    {view.policy.description}
                                </Text>
                            )}
                        </Box>
                    </Group>
                    <Group wrap="nowrap" align="center" gap="md" style={{ flexShrink: 0 }}>
                        <Sparkline values={view.spark} color={visual.solid} />
                        <Stack gap={3} align="flex-end" style={{ width: 150 }}>
                            <StatusBadge view={view} />
                            {hasData ?
                                <Text c="dimmed" size="xs" ta="right" w="100%" lineClamp={2}>
                                    {view.turns.toLocaleString()} turns
                                    {view.topValue !== null ? ` · top: ${view.topValue}` : ''}
                                </Text>
                            :   <Text c="dimmed" size="xs" ta="right" w="100%">
                                    No classified turns
                                </Text>
                            }
                        </Stack>
                        <Chevron size={16} color="var(--bm-color-text-dimmed)" style={{ flexShrink: 0 }} />
                    </Group>
                </Group>
            </UnstyledButton>

            <Collapse in={open}>
                <Box mt="md" pt="md" style={{ borderTop: '1px solid var(--bm-color-border)' }}>
                    <Group justify="space-between" align="center" mb="xs">
                        <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
                            Verdict distribution
                        </Text>
                        <Text size="xs" c="dimmed">
                            {hasData && view.avgConfidence !== null ? `${formatPct(view.avgConfidence)} avg confidence · ` : ''}selected range
                        </Text>
                    </Group>
                    {shown.length === 0 ?
                        <Text size="sm" c="dimmed">
                            No turns classified under this policy in the current window.
                        </Text>
                    :   <Stack gap="xs">
                            {shown.map((entry) => (
                                <Box key={entry.value}>
                                    <Group justify="space-between" gap="sm" wrap="nowrap" mb={2}>
                                        <Text size="xs" truncate fw={entry.concern ? 700 : 400}>
                                            {entry.value}
                                            {entry.concern ? ' ⚑' : ''}
                                        </Text>
                                        <Text size="xs" c="dimmed" ff="monospace">
                                            {entry.count.toLocaleString()}
                                        </Text>
                                    </Group>
                                    <Box style={{ height: 4, background: 'var(--bm-color-surface)', borderRadius: 2 }}>
                                        <Box
                                            style={{
                                                height: '100%',
                                                width: `${maxCount > 0 ? (entry.count / maxCount) * 100 : 0}%`,
                                                background: entry.concern ? 'var(--bm-color-danger)' : 'var(--bm-color-accent-neutral)',
                                                borderRadius: 2,
                                            }}
                                        />
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    }
                    <Group gap="lg" mt="md">
                        {hasData && (
                            <Anchor component={Link} to={detailRoute(view.policy.id)} size="sm" fw={600}>
                                View classified turns →
                            </Anchor>
                        )}
                        <Anchor component={Link} to={EDIT_ROUTE} size="sm" fw={600}>
                            Edit policy →
                        </Anchor>
                    </Group>
                </Box>
            </Collapse>
        </SurfaceCard>
    );
}
